import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchSnProductMappingList } from '#/api/aftersalesMgmt/snProductMapping/fetchList';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import SnProductMappingStatusBadge
  from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingStatusBadge';
import {
  mapMtrSnMappingRow,
  sortMtrSnMappings,
} from '#/modules/imsMgmt/settings/mtr/helper/snMappings';
import { snProductMappingListQueryKey } from '#/utils/queryKeys';

const EMPTY_PAGE = {
  current_page: 1,
  page_size: 20,
  total_count: 0,
  total_page: 1,
};

function buildInitialSelection(rows) {
  const rowSelection = {};
  const selectedById = {};
  (rows ?? []).forEach((row) => {
    if (!row?.id) {
      return;
    }
    const id = String(row.id);
    rowSelection[id] = true;
    selectedById[id] = mapMtrSnMappingRow(row);
  });
  return { rowSelection, selectedById };
}

const MtrSnMappingPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedRows,
  blockedIds,
  onConfirm,
  onListError,
  isSaving = false,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const initialSelection = useMemo(
    () => buildInitialSelection(initialSelectedRows),
    [initialSelectedRows],
  );
  const [selectedById, setSelectedById] = useState(initialSelection.selectedById);

  const listQuery = useMemo(() => ({
    page,
    pageSize,
    keyword,
  }), [keyword, page, pageSize]);

  const listQueryResult = useQuery({
    queryKey: snProductMappingListQueryKey(listQuery),
    queryFn: () => fetchSnProductMappingList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => {
    const data = listQueryResult.data?.data ?? [];
    return data
      .map(mapMtrSnMappingRow)
      .filter((row) => row.status === 'ACTIVE' && !blockedIds?.has(row.id));
  }, [blockedIds, listQueryResult.data?.data]);

  const columns = useMemo(() => ([
    createSelectColumn({ enableMultiSelection: true, showHeaderCheckbox: true }),
    {
      header: '產品代碼',
      accessorKey: 'productCode',
      size: 160,
    },
    {
      header: '產品名稱',
      accessorKey: 'productName',
      size: 240,
      meta: { cellClassName: 'td_left' },
    },
  ]), []);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id]) {
          next[id] = row;
        } else {
          delete next[id];
        }
      });
      return next;
    });
  }, [rows]);

  const selectedRows = useMemo(
    () => Object.values(selectedById).filter((row) => row?.id && !blockedIds?.has(row.id)),
    [blockedIds, selectedById],
  );
  const rowSelection = useMemo(() => {
    const selection = {};
    selectedRows.forEach((row) => {
      selection[String(row.id)] = true;
    });
    return selection;
  }, [selectedRows]);

  useEffect(() => {
    if (!listQueryResult.isError) {
      onListError?.('');
      return;
    }
    const status = listQueryResult.error?.response?.status;
    onListError?.(status === 403
      ? '沒有物料序號讀取權限，無法新增'
      : '無法載入物料序號候選');
  }, [listQueryResult.error, listQueryResult.isError, onListError]);

  const reviewRows = useMemo(
    () => sortMtrSnMappings(selectedRows),
    [selectedRows],
  );

  function handleNext() {
    setReviewOpen(true);
  }

  function handleReviewBack() {
    if (isSaving) {
      return;
    }
    setReviewOpen(false);
  }

  function handleConfirm() {
    if (isSaving) {
      return;
    }
    onConfirm(reviewRows);
  }

  function handleCancel() {
    if (isSaving) {
      return;
    }
    setReviewOpen(false);
    onOpenChange(false);
  }

  const headerSearch = (
    <div className="w-full max-w-md sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={() => {
          setPage(1);
          setKeyword(searchKeyword);
        }}
        placeholder="搜尋產品代碼或名稱"
        className="min-w-0 w-full text-foreground"
      />
    </div>
  );

  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={onOpenChange}
        title="選擇物料序號"
        description="只能新增啟用中的物料序號。已停用的既有關聯可在卡片上保留或移除。"
        headerVariant="primary"
        headerEnd={headerSearch}
        showCloseButton={false}
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={false}
        closeOnEscape={!reviewOpen && !isSaving}
        onCancel={handleCancel}
        onConfirm={handleNext}
        confirmText="下一步"
        cancelText="取消"
        confirmDisabled={isSaving}
      >
        {listQueryResult.isError ? (
          <p className="mb-2 text-sm text-destructive">無法載入物料序號候選，請確認權限後再試。</p>
        ) : null}
        {!listQueryResult.isLoading && !listQueryResult.isError && rows.length === 0 ? (
          <p className="mb-2 text-sm text-muted-foreground">這一頁沒有可新增的啟用中物料序號。</p>
        ) : null}
        <ListTablePanel
          variant="plain"
          columns={columns}
          rows={rows}
          pagination={listQueryResult.data?.page ?? EMPTY_PAGE}
          isLoading={listQueryResult.isLoading}
          isError={listQueryResult.isError}
          onPageChange={setPage}
          onPageSizeChange={(nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          }}
          enableMultiSelection
          paginationClassName="px-0 py-2"
          initialRowSelection={rowSelection}
          onRowSelectionChange={handleRowSelectionChange}
          searchPlaceholder="搜尋產品代碼或名稱"
        />
      </AppDialog>
      <AppDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        title="確定綁定物料序號"
        description="確定後會立即寫入這份清單。"
        headerVariant="primary"
        showCloseButton={false}
        closeOnOutsideClick={false}
        closeOnEscape={!isSaving}
        onOpenChange={(nextOpen) => {
          if (isSaving) {
            return;
          }
          setReviewOpen(nextOpen);
        }}
        onCancel={handleReviewBack}
        onConfirm={handleConfirm}
        confirmText={isSaving ? '儲存中…' : '確定綁定'}
        cancelText="返回"
        confirmDisabled={isSaving}
      >
        {reviewRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            尚未選擇物料序號。確定綁定後會解除全部關聯。
          </p>
        ) : (
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[24rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">狀態</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">產品代碼</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">產品名稱</th>
                </tr>
              </thead>
              <tbody>
                {reviewRows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-200">
                    <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                      <span className="inline-flex justify-center">
                        <SnProductMappingStatusBadge status={row.status} size="sm" />
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-900">{row.productCode || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-900">{row.productName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AppDialog>
    </>
  );
};

MtrSnMappingPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedRows: PropTypes.arrayOf(PropTypes.object),
  blockedIds: PropTypes.instanceOf(Set),
  onConfirm: PropTypes.func.isRequired,
  onListError: PropTypes.func,
  isSaving: PropTypes.bool,
};

export default MtrSnMappingPickerDialog;
